// Engine orders (Management › Planning & Delivery › Engine Orders): the boats past 70% assembly whose
// engines aren't installed yet (the Overview's Engine Install isn't Yes), each with two ticks kept in the
// app's own database by SAP, with who ticked them and when:
//   • Called sales — Sales has been called about the engines. Ticking it puts the boat's Engine Install
//     on the Overview to Hold (the value it had is kept); unticking puts that value back, as long as the
//     Overview still says Hold (someone may have changed it since).
//   • Engines delivered to shipyard — the engines have arrived at the yard.
// The Overview can say so first: a boat whose Engine Install is already Hold (set there by hand) has had
// Sales called, and one at Yes has its engines in, so they were called for and delivered. The page shows
// those boxes ticked "from the Overview" (`effectiveEngineOrder`); they change with the Overview, not on
// the page.
// The worker (routes/engineOrders.ts) and the web page (pages/EngineOrders.tsx) share these rules.
import { z } from 'zod';
import { assemblyPctNum } from '../logic/overview.js';
/** a boat is listed once its assembly is past this share (strictly more than 70%). */
export const ENGINE_ORDER_MIN_PCT = 70;
/** the Overview's Engine Install value for an installed engine. */
const isYes = (v) => String(v ?? '').trim().toLowerCase() === 'yes';
const isHold = (v) => String(v ?? '').trim().toLowerCase() === 'hold';
/** whether a boat's engine is installed (Engine Install says Yes). */
export const engineInstalled = (v) => isYes(v.engineInstall);
/** whether a boat belongs on the Engine Orders page: still in the yard, past 70% assembly, its engines
 *  not installed yet. */
export function needsEngineOrder(v) {
    return !v.left && assemblyPctNum(v) > ENGINE_ORDER_MIN_PCT && !engineInstalled(v);
}
export const emptyEngineOrder = (sap) => ({
    sap, calledSales: false, calledSalesBy: '', calledSalesAt: '', delivered: false, deliveredBy: '', deliveredAt: '', engineBefore: '',
});
/** a tick or an untick on one boat (one or both boxes). `season` is the Overview the boat is on. */
export const EngineOrderInputSchema = z
    .object({
    sap: z.string().trim().min(1).max(50),
    season: z.string().max(20).optional(),
    calledSales: z.boolean().optional(),
    delivered: z.boolean().optional(),
})
    .strict()
    .refine((v) => v.calledSales !== undefined || v.delivered !== undefined, { message: 'Nothing to change' });
/** what the Overview's Engine Install becomes when Called sales is ticked or unticked: the value to write
 *  ('' clears the cell), or null to leave it as it is.
 *  - ticked: Hold, unless the engine is already installed (Yes) or the cell already says Hold;
 *  - unticked: the value it had before (`before`), but only while the cell still says the Hold the tick
 *    put there: anything else is someone's later change, and stays. */
export function engineInstallAfterCalledSales(calledSales, current, before) {
    if (calledSales)
        return isYes(current) || isHold(current) ? null : 'Hold';
    if (!isHold(current) || isHold(before))
        return null;
    return isYes(before) ? null : String(before ?? '').trim();
}
/** what the Overview's Engine Install already says of a boat's engines: Hold, that Sales has been called
 *  and the boat waits on them; Yes, that they're in, so they were called for and delivered. */
export function engineOrderFromOverview(engineInstall) {
    const yes = isYes(engineInstall);
    return { calledSales: yes || isHold(engineInstall), delivered: yes };
}
export function effectiveEngineOrder(o, engineInstall) {
    const ov = engineOrderFromOverview(engineInstall);
    return {
        ...o,
        calledSales: o.calledSales || ov.calledSales,
        delivered: o.delivered || ov.delivered,
        calledSalesFromOverview: !o.calledSales && ov.calledSales,
        deliveredFromOverview: !o.delivered && ov.delivered,
    };
}
//# sourceMappingURL=engineOrder.js.map